import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore, type AccountInfo } from '../stores/auth-store.js';
import { api } from '../services/api-client.js';
import SEO from '../components/SEO.js';

type LoginMode = 'phone' | 'code' | 'pick' | 'email-login' | 'email-register';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAuth = useAuthStore((s) => s.setAuth);
  const setAccounts = useAuthStore((s) => s.setAccounts);

  const initialMode = searchParams.get('mode') === 'register' ? 'email-register' as LoginMode : 'email-login' as LoginMode;
  const [mode, setMode] = useState<LoginMode>(initialMode);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [phoneLast4, setPhoneLast4] = useState('');
  const [accounts, setAccountsList] = useState<AccountInfo[]>([]);
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  const [accountType, setAccountType] = useState<'individual' | 'dealer'>('individual');
  const [form, setForm] = useState({ email: '', password: '', name: '', dealership_name: '', phone: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const codeRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Load Google Identity Services script
  useEffect(() => {
    if (document.getElementById('google-gsi')) return;
    const script = document.createElement('script');
    script.id = 'google-gsi';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      const google = (window as any).google;
      if (!google?.accounts?.id) {
        setError('Google Sign-In not loaded yet. Try again.');
        setLoading(false);
        return;
      }
      google.accounts.id.initialize({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '',
        callback: async (response: { credential: string }) => {
          try {
            const res = await api.post<{ token: string; user: any; dealership: any; is_new: boolean }>('/auth/google', {
              credential: response.credential,
            });
            if (!res.success) { setError(res.error ?? 'Google sign-in failed'); setLoading(false); return; }
            setAuth(res.data!.token, res.data!.user, res.data!.dealership);
            navigate(res.data!.is_new ? '/onboarding' : '/dashboard');
          } catch {
            setError('Network error — check your connection');
          }
          setLoading(false);
        },
      });
      google.accounts.id.prompt();
    } catch {
      setError('Google Sign-In failed');
      setLoading(false);
    }
  };

  // Auto-focus first code input when entering code mode
  useEffect(() => {
    if (mode === 'code') {
      setTimeout(() => codeRefs.current[0]?.focus(), 100);
    }
  }, [mode]);

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) { setError('Enter a valid 10-digit phone number'); return; }
    setLoading(true);
    try {
      const res = await api.post<{ message: string; phone_last4: string }>('/auth/phone/send-code', { phone: digits });
      if (!res.success) { setError(res.error ?? 'Could not send code'); return; }
      setPhoneLast4(res.data!.phone_last4);
      setCode(['', '', '', '', '', '']);
      setMode('code');
    } catch (err) {
      setError('Network error — check your connection');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeInput = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const digit = value.slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);

    if (digit && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (digit && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerifyCode(fullCode);
      }
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newCode = pasted.split('');
      setCode(newCode);
      codeRefs.current[5]?.focus();
      handleVerifyCode(pasted);
    }
  };

  const handleVerifyCode = async (fullCode: string) => {
    setError('');
    setLoading(true);
    try {
      const digits = phone.replace(/\D/g, '');
      const res = await api.post<{ accounts: AccountInfo[]; device_token?: string; phone?: string; message?: string }>(
        '/auth/phone/verify', { phone: digits, code: fullCode }
      );
      if (!res.success) { setError(res.error ?? 'Invalid code'); setCode(['', '', '', '', '', '']); codeRefs.current[0]?.focus(); return; }

      const accts = res.data!.accounts;
      if (!accts || accts.length === 0) {
        // No account — switch to register with phone pre-filled
        setMode('email-register');
        setError('');
        return;
      }

      if (accts.length === 1) {
        // Single account — auto-login
        setAccounts(accts, res.data!.device_token ?? undefined);
        navigate('/dashboard');
      } else {
        // Multiple accounts — show picker
        setAccountsList(accts);
        setDeviceToken(res.data!.device_token ?? null);
        setMode('pick');
      }
    } catch (err) {
      setError('Network error — check your connection');
    } finally {
      setLoading(false);
    }
  };

  const handlePickAccount = (index: number) => {
    setAccounts(accounts, deviceToken ?? undefined);
    useAuthStore.getState().switchAccount(index);
    navigate('/dashboard');
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'email-login') {
        const res = await api.post<{ token: string; user: any; dealership: any }>('/auth/login', {
          email: form.email, password: form.password,
        });
        if (!res.success) { setError(res.error ?? 'Login failed'); return; }
        setAuth(res.data!.token, res.data!.user, res.data!.dealership);
      } else {
        if (!form.name) { setError('Name is required'); return; }
        if (accountType === 'dealer' && !form.dealership_name) { setError('Business name is required'); return; }
        const phoneDigits = (form.phone || phone).replace(/\D/g, '');
        const res = await api.post<{ token: string; user: any; dealership: any }>('/auth/register', {
          email: form.email, password: form.password, name: form.name,
          account_type: accountType,
          ...(phoneDigits.length >= 10 ? { phone: phoneDigits } : {}),
          ...(accountType === 'dealer' ? { dealership_name: form.dealership_name } : {}),
        });
        if (!res.success) { setError(res.error ?? 'Registration failed'); return; }
        setAuth(res.data!.token, res.data!.user, res.data!.dealership);
      }
      navigate(mode === 'email-register' ? '/onboarding' : '/dashboard');
    } catch (err) {
      setError('Network error — check your connection');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-900 flex flex-col items-center justify-center px-6">
      <SEO title="Sign In" description="Sign in to AutoCarPost to manage your car listings and deals." path="/login" />
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-white tracking-tight">AutoCarPost</h1>
          <p className="text-surface-500 mt-2 text-sm">Scan. Snap. Sell.</p>
        </div>

        {/* ── Phone Number Entry ── */}
        {mode === 'phone' && (
          <>
            <form onSubmit={handleSendCode} className="space-y-4">
              <div>
                <label className="block text-surface-400 text-xs font-medium mb-2 uppercase tracking-wider">Phone number</label>
                <input
                  type="tel"
                  placeholder="(555) 123-4567"
                  className="input w-full text-center text-xl tracking-wider"
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  autoFocus
                />
              </div>
              {error && <p className="text-red-400 text-sm text-center">{error}</p>}
              <button type="submit" className="btn-primary w-full" disabled={loading}>
                {loading ? 'Sending...' : 'Send Code'}
              </button>
            </form>
            <p className="text-surface-600 text-xs text-center mt-4">We'll text you a 6-digit code to sign in.</p>
            <button
              onClick={() => { setMode('email-login'); setError(''); }}
              className="btn-ghost w-full mt-3 text-xs text-surface-500"
            >
              Use email & password instead
            </button>
          </>
        )}

        {/* ── Code Entry ── */}
        {mode === 'code' && (
          <>
            <div className="text-center mb-6">
              <p className="text-surface-400 text-sm">Enter the code sent to</p>
              <p className="text-white font-medium">***-***-{phoneLast4}</p>
            </div>
            <div className="flex justify-center gap-2 mb-6" onPaste={handleCodePaste}>
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={el => { codeRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className="w-12 h-14 text-center text-2xl font-bold bg-surface-800 border-2 border-surface-700 rounded-xl text-white focus:border-brand-500 focus:outline-none transition-colors"
                  value={digit}
                  onChange={(e) => handleCodeInput(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                />
              ))}
            </div>
            {error && <p className="text-red-400 text-sm text-center mb-4">{error}</p>}
            {loading && <p className="text-brand-400 text-sm text-center mb-4">Verifying...</p>}
            <button
              onClick={() => { setMode('phone'); setError(''); }}
              className="btn-ghost w-full text-xs text-surface-500"
            >
              Try a different number
            </button>
          </>
        )}

        {/* ── Account Picker (multi-account) ── */}
        {mode === 'pick' && (
          <>
            <p className="text-surface-400 text-sm text-center mb-4">Pick an account to continue</p>
            <div className="space-y-2">
              {accounts.map((acct, i) => (
                <button
                  key={acct.user.id}
                  onClick={() => handlePickAccount(i)}
                  className="w-full flex items-center gap-3 p-4 bg-surface-800 border border-surface-700 rounded-xl hover:border-brand-500 transition-colors text-left"
                >
                  <div className="w-10 h-10 bg-brand-600 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0">
                    {acct.dealership.name[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-medium text-sm truncate">{acct.dealership.name}</p>
                    <p className="text-surface-500 text-xs truncate">{acct.user.email} · {acct.user.role}</p>
                  </div>
                  <span className="ml-auto text-surface-600 text-xs px-2 py-0.5 bg-surface-700 rounded-full shrink-0">
                    {acct.dealership.account_type === 'dealer' ? 'Dealer' : 'Individual'}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* ── Email Login ── */}
        {mode === 'email-login' && (
          <>
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <input type="email" placeholder="Email" className="input w-full" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} required autoFocus />
              <input type="password" placeholder="Password" className="input w-full" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} />
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button type="submit" className="btn-primary w-full" disabled={loading}>
                {loading ? '...' : 'Sign In'}
              </button>
            </form>
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-surface-700" />
              <span className="text-surface-600 text-xs">or</span>
              <div className="flex-1 h-px bg-surface-700" />
            </div>
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl bg-white text-gray-800 font-medium text-sm hover:bg-gray-100 transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 18 18"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 2.58 9 2.58Z" fill="#EA4335"/></svg>
              Continue with Google
            </button>
            <button onClick={() => { setMode('email-register'); setError(''); }} className="btn-ghost w-full mt-3 text-sm">
              Need an account? Register
            </button>
            <button onClick={() => { setMode('phone'); setError(''); }} className="btn-ghost w-full mt-1 text-xs text-surface-500">
              Sign in with phone instead
            </button>
          </>
        )}

        {/* ── Email Register ── */}
        {mode === 'email-register' && (
          <>
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setAccountType('individual')}
                  className={`p-3 rounded-xl text-center text-sm font-medium transition-colors border-2 ${
                    accountType === 'individual' ? 'border-brand-500 bg-brand-500/10 text-brand-400' : 'border-surface-700 bg-surface-800 text-surface-400'
                  }`}>
                  Selling a Car
                </button>
                <button type="button" onClick={() => setAccountType('dealer')}
                  className={`p-3 rounded-xl text-center text-sm font-medium transition-colors border-2 ${
                    accountType === 'dealer' ? 'border-brand-500 bg-brand-500/10 text-brand-400' : 'border-surface-700 bg-surface-800 text-surface-400'
                  }`}>
                  I'm a Dealer
                </button>
              </div>
              <input type="text" placeholder="Your name" className="input w-full" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
              {accountType === 'dealer' && (
                <input type="text" placeholder="Business name" className="input w-full" value={form.dealership_name}
                  onChange={(e) => setForm({ ...form, dealership_name: e.target.value })} />
              )}
              <input type="tel" placeholder="Phone (buyers will see this)" className="input w-full" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })} />
              <input type="email" placeholder="Email" className="input w-full" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              <input type="password" placeholder="Password (8+ characters)" className="input w-full" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} />
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button type="submit" className="btn-primary w-full" disabled={loading}>
                {loading ? '...' : 'Create Account'}
              </button>
            </form>
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-surface-700" />
              <span className="text-surface-600 text-xs">or</span>
              <div className="flex-1 h-px bg-surface-700" />
            </div>
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl bg-white text-gray-800 font-medium text-sm hover:bg-gray-100 transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 18 18"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 2.58 9 2.58Z" fill="#EA4335"/></svg>
              Sign up with Google
            </button>
            <button onClick={() => { setMode('email-login'); setError(''); }} className="btn-ghost w-full mt-3 text-sm">
              Have an account? Sign in
            </button>
            <button onClick={() => { setMode('phone'); setError(''); }} className="btn-ghost w-full mt-1 text-xs text-surface-500">
              Sign in with phone instead
            </button>
            <p className="text-surface-600 text-xs text-center mt-4">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="text-surface-400 underline">Terms</Link> and{' '}
              <Link to="/privacy" className="text-surface-400 underline">Privacy Policy</Link>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
